import { useState } from "react";
import { Breadcrumb, Button } from "flowbite-react";
import { HiHome, HiPlus } from "react-icons/hi";
import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import { useTranslation } from "react-i18next";
import { useGetEventStatusesQuery, useCreateEventStatusMutation } from "@/api/locationApi/eventStatus";
import { getErrorMessage } from "@/utils/requestUtils";
import { toast } from "@/lib/toast";
import EventStatusListTable from "./EventStatusListTable";
import EventStatusFormDialog from "./EventStatusFormDialog";
import { EventStatus, QueryParams } from "@/sharedTypes";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isCompanyAdminUser } from "@/constants/agent";

export default function EventStatusList() {
  const { t } = useTranslation(["temporalActivity", "common"]);
  const user = useCurrentUser();

  const [queryParams, setQueryParams] = useState<QueryParams>({
    filters: {},
    pagination: { page: 1, pageSize: 15 },
  });

  const { data: eventStatusesData } = useGetEventStatusesQuery(queryParams);
  const [createEventStatus] = useCreateEventStatusMutation();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const handleCreate = (data: EventStatus) => {
    createEventStatus(data)
      .unwrap()
      .then(() => {
        toast.success(t("temporalActivity:eventStatus.createdSuccess"));
        setIsCreateModalOpen(false);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handlePageChange = (page: number) => {
    setQueryParams((prev) => ({ ...prev, pagination: { ...prev.pagination, page } }));
  };

  return (
    <NavbarSidebarLayout>
      <div className="h-screen overflow-y-auto pb-20">
        <div className="mb-6 grid grid-cols-1 gap-y-6 px-4 pt-6 dark:border-gray-700 dark:bg-gray-900">
          <div className="col-span-full">
            <Breadcrumb className="mb-4">
              <Breadcrumb.Item href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">{t("common:home")}</span>
                </div>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("temporalActivity:eventStatus.title")}</Breadcrumb.Item>
            </Breadcrumb>

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                  {t("temporalActivity:eventStatus.title")}
                </h1>
                <p className="text-gray-500">{t("temporalActivity:eventStatus.manage")}</p>
              </div>
              {isCompanyAdminUser(user) && (
                <Button color="primary" onClick={() => setIsCreateModalOpen(true)}>
                  <HiPlus className="mr-2 h-5 w-5" />
                  {t("temporalActivity:eventStatus.create")}
                </Button>
              )}
            </div>
          </div>

          <EventStatusListTable
            data={eventStatusesData?.items || []}
            pagination={
              eventStatusesData?.pagination
                ? {
                    pageSize: eventStatusesData.pagination.pageSize || 15,
                    pageIndex: eventStatusesData.pagination.pageIndex ?? eventStatusesData.pagination.page ?? 1,
                    totalPages: eventStatusesData.pagination.totalPages ?? 1,
                    totalItems: eventStatusesData.pagination.totalItems ?? eventStatusesData.pagination.total ?? 0,
                  }
                : { pageSize: 15, pageIndex: 1, totalPages: 1, totalItems: 0 }
            }
            onPageChange={handlePageChange}
          />
        </div>
      </div>

      <EventStatusFormDialog
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreate}
      />
    </NavbarSidebarLayout>
  );
}
